import React, { useEffect, useState, useRef } from "react";
import { useBlocker } from "react-router-dom";
import PropTypes from "prop-types";
import { ThemeProvider, styled } from "@mui/material/styles";
import dtrTheme from "components/LearnPoint/DTR/dtrTheme";
import classes from "./HandOffReport.module.css";
import { fontStyles } from "components/Common/commonStyles";
import BackdropComponent from "components/Spinner/Spinner";
import AIPrompt from "components/Common/AIPrompt/AIPrompt";
import SnackBar from "components/SnackBar/SnackBar";
import {
  Box,
  Button,
  Grid,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import FieldLabel from "components/LearnPoint/DTR/FieldLabel";
import {
  getHandOffNotes,
  saveHandOffReportWithAttachments,
  getDTRAttachmentsByParentId,
  sendHandOffEmail,
  previewHandOffEmail,
  downloadAttachmentImageAsBlobUrl,
} from "services/learnPointUserServices";
import { isEmptyString } from "components/RequestForAccess/RequestForAccess";
import SaveOrSendButton from "components/LearnPoint/DTR/SaveOrSendButton";
// ASTT-7319: shared with TrainingNotes.js's own identical use.
import EmailPreviewDialog from "components/LearnPoint/DTR/EmailPreviewDialog";
import DtrEmailDistro from "components/LearnPoint/DTR/DtrEmailDistro";
// Shared with TrainingNotes.js. editorKey/revertKey (createBoundedChangeHandler below) is
// pre-existing, unaffected work.
import RichTextField, {
  stripHtml,
  sanitizeEditorHtml,
} from "components/LearnPoint/DTR/RichTextEditorField";
import DTRAttachmentList from "components/LearnPoint/DTR/DTRAttachmentList";
import DTRAttachmentSummary from "components/LearnPoint/DTR/DTRAttachmentSummary";
import UnsavedChangesBlockerDialog from "components/LearnPoint/DTR/UnsavedChangesBlockerDialog";

const MAX_LENGTH = 4000;
const STATUS_OPTIONS = ["Complete", "Pending Completion"];

const roundedInputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    // ASTT-7319 follow-up: was 24 (pill-style) -- matches
    // RichTextEditorField.js's actual radius now, per the same fix
    // already applied to TrainingNotes.js's Curriculum Status/DTR Date/
    // Class Roster Ids/Email Distro. Used by "Final TTEC Fundamentals
    // Status"/"Final Curriculum Status" below.
    borderRadius: "4px",
    border: "1px solid #d3d3d3",
    backgroundColor: "#fff",
    "& fieldset": { border: "none" },
  },
};

// ASTT-7319: see TrainingNotes.js's identical comment -- fontFamily/
// letterSpacing were hardcoded literals duplicating the actual
// established fontStyles convention.
const CustomButton = styled(Button)(() => ({
  ...fontStyles,
  fontSize: "12px",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  color: "white",
  backgroundColor: "#3299FF",
  "&:hover": {
    backgroundColor: "#2878CC",
  },
}));

const CustomOutlinedButton = styled(Button)(() => ({
  ...fontStyles,
  fontSize: "12px",
  borderRadius: "8px",
  fontWeight: "600",
  textTransform: "none",
  borderColor: "#3299FF",
  color: "#3299FF",
  padding: "6px 20px",

  "&:hover": {
    borderColor: "#3299FF",
    backgroundColor: "#E0F2FF",
  },
}));

export default function HandOffReport(props) {
  const { rosterId, onDirtyChange, classRequestId, reportType, onReportTypeChange, onHandOffSaved, rosterStatusList } = props;
  // ASTT-7319: moved to shared dtrTheme.js -- see TrainingNotes.js's
  // identical comment.
  const theme = dtrTheme;

  const [activeEditorField, setActiveEditorField] = useState(null);

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [ShowAIPrompt, setShowAIPrompt] = useState(false);
  const [currentNotes, setCurrentNotes] = useState("");
  const [aiNotes, setAiNotes] = useState("");
  const [fieldLableName, setFieldLableName] = useState("");
  const [fieldMaxLenght, setFieldMaxLenght] = useState(0);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isDistroEdit, setIsDistroEdit] = useState(false);
  const [distroId, setDistroId] = useState(0);
  const initialEmailDistroValue = {
    LP_DTRDistroId: 0,
    DistroName: "",
    DistroEmail: "",
  };

  const [emailDistroValue, setEmailDistroValue] = useState(
    initialEmailDistroValue,
  );

  const updateEmailDistro = (value) => {
    setEmailDistroValue(value);
    setIsDistroEdit(false);
  };

  // ASTT-7178: reportType/onReportTypeChange now come from props (lifted
  // up to DtrDetails.js so ClassDetails.js's roster picker can also read
  // the current report type for color-coding) -- this component no
  // longer owns this as local state.
  // FinalEquipmentStatus/finalClientIdStatus/finalTtecIdStatus
  // manual number-entry fields removed -- these three readiness numbers are
  // now computed from milestone status (same as DTR's Current numbers) at
  // send time, not entered by hand here.
  const [fundamentalsStatus, setFundamentalsStatus] = useState(null);
  const [curriculumStatus, setCurriculumStatus] = useState(null);
  const [challenges, setChallenges] = useState("");
  const [successes, setSuccesses] = useState("");
  const [readlinessNotes, setReadlinessNotes] = useState("");
  const [classRosterId, setClassRosterId] = useState("");

  // Deferred-upload attachment state, mirroring TrainingNotes.js.
  const [handOffReportId, setHandOffReportId] = useState(0);
  // ASTT-7319: Preview button/dialog -- see TrainingNotes.js's identical
  // state comment for the full reasoning.
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [pendingImages, setPendingImages] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingFilesResetKey, setPendingFilesResetKey] = useState(0);

  const existingFileAttachments = existingAttachments.filter(
    (a) => a.AttachmentKind === "FileAttachment",
  );

  // Re-filters pendingImages against each field's CURRENT html so only images still
  // actually referenced get uploaded on Save.
  const currentFieldValuesByName = {
    Successes: successes,
    Challenges: challenges,
    ReadinessNotes: readlinessNotes,
  };
  const referencedPendingImages = pendingImages.filter((p) => {
    const currentValue = currentFieldValuesByName[p.fieldName] || "";
    return currentValue.includes(`pending:${p.placeholderToken}`);
  });

  const fileCount = existingFileAttachments.length + pendingFiles.length;
  const fileSizeBytes =
    existingFileAttachments.reduce((sum, a) => sum + (a.FileSizeBytes || 0), 0) +
    pendingFiles.reduce((sum, p) => sum + (p.file?.size || 0), 0);

  // ASTT-7178: proactive Save/Send guard -- reuses rosterStatusList
  // (bubbled up from ClassDetails.js's own roster-status fetch, which
  // already covers every roster this component could have selected; no
  // separate call here). Blocks the exact same condition
  // usp_LP_SaveHandOffReport enforces server-side (any selected roster
  // already Sent for this report type) -- this is what actually disables
  // the button and explains why, rather than only surfacing a generic
  // error after a failed round trip.
  const selectedRosterIdList = (rosterId || "")
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
  const conflictingRosterIds = (rosterStatusList || [])
    .filter(
      (r) =>
        selectedRosterIdList.includes(String(r.rosterId)) &&
        r.handOffStatus === "Sent",
    )
    .map((r) => r.rosterId);
  const hasSentConflict = conflictingRosterIds.length > 0;

  const handleEditorBlur = () => {
    setActiveEditorField(null);
  };

  const [challengesRevertKey, setChallengesRevertKey] = useState(0);
  const [successesRevertKey, setSuccessesRevertKey] = useState(0);
  const [readinessRevertKey, setReadinessRevertKey] = useState(0);

  const createBoundedChangeHandler =
    (setValue, maxLength, bumpRevertKey, fieldLabel) => (e) => {
      const newValue = e.htmlValue ?? "";
      const plainLength = stripHtml(newValue).length;

      if (plainLength > maxLength) {
        bumpRevertKey((k) => k + 1);
        showSnack(
          `${fieldLabel} has reached the ${maxLength}-character limit.`,
          "warning",
        );
        return;
      }
      markDirty();
      setValue(newValue);
    };

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      if (!rosterId) {
        // ASTT-7319 follow-up: clearing the Class Roster Ids selection
        // wasn't resetting Challenges/Successes/Readiness Notes (or
        // Email Distro/existing attachments/read-only state) at all --
        // this guard correctly skipped fetching for an empty rosterId,
        // but never had a matching "and reset everything" branch for
        // when it goes empty specifically because the user cleared it.
        // populateData(null) already implements exactly that reset (its
        // own "no record found" branch), so this reuses it directly
        // rather than duplicating the same field-by-field logic here.
        if (!cancelled) populateData(null);
        return;
      }
      setLoading(true);

      const classRosterIds = getRosterIdString(rosterId);
      setClassRosterId(classRosterIds);

      try {
        const response = await getHandOffNotes(classRosterIds, reportType);
        if (cancelled) return;
        if (response?.statusCode === 200) {
          populateData(response?.data?.[0]);
        }
      } catch (error) {
        if (!cancelled) console.error("Failed to fetch data:", error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [rosterId]); // run on initial load; report-type switches go through the toggle handler

  const handleReportTypeChange = async (event, value) => {
    if (value) {
      onReportTypeChange(value);
      setLoading(true);
      const response = await getHandOffNotes(classRosterId, value);
      if (response?.statusCode === 200) {
        populateData(response?.data?.[0]);
      }
      // Deactivate the open field so it picks up the newly-loaded report type's content on
      // next click (RichTextEditorField.js freezes its live value once activated).
      setActiveEditorField(null);
      setPendingImages([]);
      setPendingFiles([]);
      setPendingFilesResetKey((k) => k + 1);
      clearDirty();
    }

    setLoading(false);
  };

  const populateData = (data) => {
    if (!data) {
      // No record found
      setFundamentalsStatus(null);
      setCurriculumStatus(null);
      setChallenges("");
      setSuccesses("");
      setReadlinessNotes("");
      setEmailDistroValue(initialEmailDistroValue);
      setIsReadOnly(false);
      setHandOffReportId(0);
      return;
    }

    setFundamentalsStatus(data.FinalTTECFundamentalsStatus || null);
    setCurriculumStatus(data.FinalCurriculumStatus || null);
    setChallenges(data.Challenges || "");
    setSuccesses(data.Successes || "");
    setReadlinessNotes(data.ReadinessNotes || "");
    setEmailDistroValue({
      LP_DTRDistroId: data.EmailDistroId || 0,
      DistroName: data.EmailDistro ? data.EmailDistro : "",
      DistroEmail: data.EmailDistro || "",
    });
    setDistroId(data.EmailDistroId || 0);
    setIsReadOnly(data.HandOffReportStatus == "Sent");
    setHandOffReportId(data.HandOffReportId || 0);
  };

  const showSnack = (message, severity) => {
    setSnack({
      open: true,
      message: message,
      severity: severity,
    });
  };

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "",
  });

  const snackReset = () => {
    setSnack({
      open: false,
      message: "",
      severity: "",
    });
  };

  const getRosterIdString = (rosterId) => {
    return Array.isArray(rosterId)
      ? rosterId.map((roster) => String(roster.rosterId)).join(",")
      : String(rosterId);
  };

  // Fetches existing attachments whenever handOffReportId changes. A successful save also
  // sets existingAttachments directly from that save's response (see saveAsDraft).
  useEffect(() => {
    let cancelled = false;

    const fetchExistingAttachments = async () => {
      if (!handOffReportId) {
        if (!cancelled) setExistingAttachments([]);
        return;
      }
      const response = await getDTRAttachmentsByParentId(null, handOffReportId);
      if (cancelled) return;
      if (response?.statusCode === 200 && Array.isArray(response.data)) {
        setExistingAttachments(response.data);
      } else if (response?.statusCode === 404) {
        setExistingAttachments([]);
      } else {
        // Real failure, not "no attachments" — surface it explicitly.
        setExistingAttachments([]);
        showSnack(
          "Could not load existing attachments for this entry. They may still exist — please refresh or contact support if this persists.",
          "error",
        );
      }
    };

    fetchExistingAttachments();
    return () => {
      cancelled = true;
    };
  }, [handOffReportId]);

  // ASTT-7319 follow-up: the saved-image rehydration that used to live
  // here was WRONG -- see TrainingNotes.js's identical comment for the
  // full reasoning (in short: calling setSuccesses/etc. directly meant
  // the next Save/Send resubmitted the transient blob: URL as the new
  // saved content, silently dropping the image from outgoing emails).
  // Moved into RichTextField itself (RichTextEditorField.js);
  // existingAttachments/downloadAttachmentImageAsBlobUrl are now passed
  // down as props instead.

  // Tracks whether there's anything unsaved so we can warn before the user navigates away.
  const isDirtyRef = useRef(false);

  const markDirty = () => {
    if (!isDirtyRef.current) {
      isDirtyRef.current = true;
      onDirtyChange?.(true);
    }
  };

  const clearDirty = () => {
    isDirtyRef.current = false;
    onDirtyChange?.(false);
  };

  // Fired when an image is pasted/inserted (blob: preview already shown) — stages the raw
  // File until Save; nothing is uploaded here.
  const handleImagePending = (file, meta) => {
    markDirty();
    setPendingImages((prev) => [
      ...prev,
      {
        file,
        placeholderToken: meta.placeholderToken,
        fieldName: meta.fieldName,
        kind: "InlineImage",
      },
    ]);
  };

  const handlePendingFilesChange = (files) => {
    setPendingFiles(files);
    if (files.length > 0) markDirty();
  };

  const handleExistingAttachmentRemoved = (attachmentId) => {
    setExistingAttachments((prev) =>
      prev.filter((a) => a.LP_DTRAttachmentId !== attachmentId),
    );
  };

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (!isDirtyRef.current) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // Covers in-app navigation away from this page — see TrainingNotes.js's identical block.
  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirtyRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );

  const handleSaveAsDraft = async () => {
    setLoading(true);
    if (validateForEmptyFields()) {
      setLoadingMessage(
        referencedPendingImages.length > 0 || pendingFiles.length > 0
          ? "Saving content and uploading attachments..."
          : "Saving content...",
      );
      const result = await saveAsDraft();
      if (result) {
        showSnack("Hand-off report saved successfully.", "success");
        // ASTT-7178: notifies DtrDetails.js so ClassDetails.js's roster
        // picker can re-fetch and recolor -- without this, a roster's
        // Pending -> Draft transition (or a content edit to an existing
        // Draft) wasn't reflected in the picker until something else
        // (a report-type toggle) happened to trigger a refetch anyway.
        // Mirrors TrainingNotes.js's onDtrSaved exactly.
        onHandOffSaved?.();
      } else {
        showSnack("Internal error occured.", "error");
      }
    }
    setLoading(false);
    setLoadingMessage("");
  };
  const validateForEmptyFields = () => {
    if (!rosterId) {
      showSnack("Roster ID is missing. Please select a roster.", "error");
      return false;
    } else if (hasSentConflict) {
      // ASTT-7178: defense-in-depth -- the button itself is already
      // disabled in this state, but this guards any path that could
      // still reach here (e.g. a stale rosterStatusList not yet
      // reflecting a just-changed selection).
      showSnack(
        `Roster(s) ${conflictingRosterIds.join(", ")} already ha${conflictingRosterIds.length === 1 ? "s" : "ve"} a Sent Hand-Off report for this report type. Remove ${conflictingRosterIds.length === 1 ? "it" : "them"} from the selection to save.`,
        "error",
      );
      return false;
    } else if (isEmptyString(stripHtml(successes))) {
      showSnack("Cannot save empty spaces for successes.", "warning");
      return false;
    } else if (isEmptyString(stripHtml(challenges))) {
      showSnack("Cannot save empty spaces for challenges.", "warning");
      return false;
    } else if (isEmptyString(stripHtml(readlinessNotes))) {
      showSnack("Cannot save empty spaces for readiness notes.", "warning");
      return false;
    }
    return true;
  };

  const saveAsDraft = async (handOffReportStatus = "Draft") => {
    const payload = prepareSaveAsDraftPayload(handOffReportStatus);
    const combinedPendingAttachments = [...referencedPendingImages, ...pendingFiles];

    const response = await saveHandOffReportWithAttachments(
      payload,
      combinedPendingAttachments,
    );

    if (response?.statusCode === 200) {
      const savedHandOffId = response?.data?.handOffReportId || 0;
      setHandOffReportId(savedHandOffId);
      // Authoritative — replaces existingAttachments with exactly what the server confirms.
      if (Array.isArray(response?.data?.attachments)) {
        setExistingAttachments(response.data.attachments);
      }

      // ASTT-7319 follow-up: same reorder as TrainingNotes.js's identical
      // fix -- setPendingImages([])/setActiveEditorField(null) used to
      // run before this refetch, not after. See that fix's own comment
      // for the full race-condition reasoning (confirmed reproducible:
      // clearing pendingImages removes what rehydratePendingImageSrcs
      // needs to rebuild a live blob: URL, and deactivating switches to a
      // preview that renders raw HTML directly -- in the gap before this
      // refetch resolves, the field's value still held stale
      // "pending:{token}" markers with nothing left able to resolve them).
      if (referencedPendingImages.length > 0) {
        const refreshed = await getHandOffNotes(classRosterId, reportType);
        if (refreshed?.statusCode === 200) {
          populateData(refreshed?.data?.[0]);
        }
      }

      setActiveEditorField(null);
      setPendingImages([]);
      setPendingFiles([]);
      setPendingFilesResetKey((k) => k + 1);

      clearDirty();
      return savedHandOffId;
    }
    return 0;
  };

  const prepareSaveAsDraftPayload = (handOffReportStatus = "Draft") => {
    let payloads = [];
    if (rosterId) {
      const classRosterIds = getRosterIdString(rosterId);
      payloads = {
        classRosterId: classRosterIds,
        classRequestId,
        reportTypeId: parseInt(reportType, 10),
        // FinalEquipmentStatus/finalClientIdStatus/finalTTECIdStatus
        // no longer sent from the UI -- computed from milestone status at
        // send time instead. usp_LP_SaveHandOffReport's OPENJSON WITH clause
        // tolerates the missing properties (they come through as NULL).
        finalTTECFundamentalsStatus: fundamentalsStatus || "",
        finalCurriculumStatus: curriculumStatus || "",
        successes: sanitizeEditorHtml(successes),
        challenges: sanitizeEditorHtml(challenges),
        readinessNotes: sanitizeEditorHtml(readlinessNotes),
        lP_DTRDistroId: emailDistroValue?.LP_DTRDistroId,
        emailDistro: emailDistroValue?.DistroEmail,
        handOffReportStatus: handOffReportStatus,
        modifiedBy: localStorage.getItem("User"),
        attachmentIdsToDelete: "",
      };
    }
    return payloads;
  };

  const handleDistroAddorEdit = (isEdit) => {
    setIsDistroEdit(isEdit);
    setShowModal(true);
  };

  // Single configuration object
  const FIELD_HANDLERS = {
    Successes: {
      getValue: () => successes,
      setValue: setSuccesses,
      maxLength: 4000,
    },
    Challenges: {
      getValue: () => challenges,
      setValue: setChallenges,
      maxLength: 4000,
    },
    ReadinessNotes: {
      getValue: () => readlinessNotes,
      setValue: setReadlinessNotes,
      maxLength: 4000,
    },
  };

  const openAIPrompt = (fieldName) => {
    const handler = FIELD_HANDLERS[fieldName];
    if (!handler) return;

    setCurrentNotes("");
    setFieldLableName(fieldName);
    setCurrentNotes(stripHtml(handler.getValue())); // strip HTML before passing to AIPrompt
    setFieldMaxLenght(handler.maxLength);
    setShowAIPrompt(true);
  };

  const handleClose = (acceptanceFlag) => {
    setShowAIPrompt(false);

    if (acceptanceFlag) {
      const handler = FIELD_HANDLERS[fieldLableName];
      if (handler) {
        handler.setValue(aiNotes);
      }
    }
  };

  const handleSendEmail = async () => {
    // ASTT-7319 follow-up: same fix as TrainingNotes.js's identical
    // crash -- emailDistroValue is null now (was an empty-but-real-
    // shaped object), via DtrEmailDistro.js's own fix propagating
    // outward through the updateEmailDistro callback.
    if ((emailDistroValue?.DistroEmail ?? "") === "") {
      showSnack("Please select email distro.", "error");
      return;
    }
    if (!validateForEmptyFields()) {
      return;
    }
    setLoading(true);

    try {
      setLoadingMessage(
        referencedPendingImages.length > 0 || pendingFiles.length > 0
          ? "Saving content and uploading attachments..."
          : "Saving content...",
      );
      const savedHandOffId = await saveAsDraft("Sent");
      if (savedHandOffId > 0) {
        setLoadingMessage("Sending email...");
        const payload = prepareMailPayload(savedHandOffId);
        const emailResponse = await sendHandOffEmail(payload);
        if (emailResponse?.statusCode === 200) {
          showSnack("Email sent successfully.", "success");
          const classRosterIds = getRosterIdString(rosterId);
          setClassRosterId(classRosterIds);
          const response = await getHandOffNotes(classRosterIds, reportType);
          if (response?.statusCode === 200) {
            populateData(response?.data?.[0]);
          }
          // ASTT-7178: same reasoning as handleSaveAsDraft's identical
          // call -- a Sent status change needs to reflect in the roster
          // picker's coloring immediately, not just on the next
          // unrelated toggle.
          onHandOffSaved?.();
        } else {
          showSnack("Internal error occured.", "error");
        }
      }
    } catch (error) {
      console.error("error occured:", error);
    } finally {
      setLoading(false);
      setLoadingMessage("");
    }
  };

  const prepareMailPayload = (savedHandOffId) => {
    const payload = {
      HandOffReportId: savedHandOffId,
      EmailTo: emailDistroValue?.DistroEmail,
      EmailCc: localStorage.getItem("EmailId"),
      EmailSubject: `Hand-Off report has been submitted by ${localStorage.getItem(
        "User",
      )}`,
    };
    return payload;
  };

  return (
    <ThemeProvider theme={theme}>
      {loading ? <BackdropComponent message={loadingMessage} /> : null}
      {ShowAIPrompt && (
        <AIPrompt
          promptOpen={ShowAIPrompt}
          handleClose={handleClose}
          currentNotes={currentNotes}
          setAiNotes={setAiNotes}
          fieldMaxLenght={fieldMaxLenght}
        />
      )}
      <Grid container id="handOffReportDetails" sx={{ p: 2 }}>
        <ToggleButtonGroup
          value={reportType}
          exclusive
          onChange={handleReportTypeChange}
          sx={{
            backgroundColor: "#fff",
            border: "1px solid #3299FF",
            borderRadius: "8px",
            p: "2px",
            mb: 3,
            "& .MuiToggleButton-root": {
              border: "none",
              borderRadius: "6px !important",
              textTransform: "none",
              fontWeight: 600,
              fontSize: "12px",
              letterSpacing: "0.10em",
              px: 3,
              py: 0.6,
              color: "#3299FF",
              "&.Mui-selected": {
                backgroundColor: "#3299FF",
                color: "#fff",
                "&:hover": { backgroundColor: "#2878CC" },
              },
            },
          }}
        >
          <ToggleButton value="0">To Nesting</ToggleButton>
          <ToggleButton value="1">To Production</ToggleButton>
        </ToggleButtonGroup>
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* Final Equipment/Client ID/TTEC ID Status manual
              number entry removed -- these are now computed from milestone
              status (Initial/Final rows in the email itself), not entered
              here. */}
          {/* ASTT-7319 follow-up: xs={2}, was xs={3} -- confirmed too wide
              relative to "Final Curriculum Status" (xs={2}, unchanged),
              since roundedInputSx's width: 100% just fills whatever
              column it's given -- matching the column width itself is
              what actually equalizes the two visually. */}
          <Grid item xs={2}>
            {/* ASTT-7319: was TextLabel -- a differently-styled shared
                component (#3F5765/bold/0.10em spacing) than the
                established RichTextField reference (#555/600 weight/
                0.04em spacing). FieldLabel matches that reference. */}
            <FieldLabel>Final TTEC Fundamentals Status</FieldLabel>
            <Autocomplete
              disablePortal
              size="small"
              disabled={isReadOnly}
              value={fundamentalsStatus}
              options={STATUS_OPTIONS}
              onChange={(event, value) => setFundamentalsStatus(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select"
                  sx={roundedInputSx}
                />
              )}
            />
          </Grid>

          <Grid item xs={2}>
            <FieldLabel>Final Curriculum Status</FieldLabel>
            <Autocomplete
              disablePortal
              size="small"
              disabled={isReadOnly}
              value={curriculumStatus}
              options={STATUS_OPTIONS}
              onChange={(event, value) => setCurriculumStatus(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Select"
                  sx={roundedInputSx}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* ASTT-7319: Challenges/Successes paired 2-per-row, matching the
            grid pattern TrainingNotes.js already uses for its own note-
            field pairs -- this screen previously stacked all three note
            fields full-width, 1-per-row, despite Challenges/Successes
            being the same kind of paired content as DTR's own fields.
            Readiness Notes has no natural pair, so it stays full-width. */}
        {/* ASTT-7319 follow-up: spacing={2} added, className={padding1Rem}
            removed from the items below -- "Final TTEC Fundamentals
            Status"'s own container uses spacing={2} with no padding class
            on its items, and MUI's spacing mechanic (negative margin on
            the container, positive padding on each item) cancels out to a
            net-zero left offset for the first item in a row. This
            container had neither the spacing prop nor that cancellation,
            so padding1Rem's 16px was pure uncancelled offset -- a real,
            traceable reason Challenges/Successes/Readiness Notes sat
            further right than Final TTEC Fundamentals Status/To Nesting,
            confirmed via the Grid mechanics themselves, not a guess. */}
        {/* ASTT-7319 follow-up 2: mb: 2 added -- padding1Rem's top/bottom
            padding also provided vertical gap between this row and its
            neighbors, which removing it (to fix the left-alignment bug)
            took away too. "Final TTEC Fundamentals Status"'s own
            container already carries this exact mb: 2; matching it here
            restores the row-to-row spacing without reintroducing the
            left-offset issue. */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {/* ASTT-7319: sm={12} md={6} added -- see TrainingNotes.js's
              identical fix comment for why. */}
          <Grid item xs={12} sm={12} md={6}>
            <RichTextField
              label="Challenges (Comprehensive)"
              isReadOnly={isReadOnly}
              value={challenges}
              editorKey={challengesRevertKey}
              onChange={createBoundedChangeHandler(
                setChallenges,
                MAX_LENGTH,
                setChallengesRevertKey,
                "Challenges",
              )}
              isActive={activeEditorField === "Challenges"}
              onActivate={() => setActiveEditorField("Challenges")}
              onDeactivate={handleEditorBlur}
              charCount={stripHtml(challenges).length}
              maxLength={MAX_LENGTH}
              onAIClick={() => openAIPrompt("Challenges")}
              aiColor="#3299FF"
              fieldName="Challenges"
              onImagePending={handleImagePending}
              pendingImages={pendingImages}
              existingAttachments={existingAttachments}
              downloadImageAsBlobUrl={downloadAttachmentImageAsBlobUrl}
            />
          </Grid>
          <Grid item xs={12} sm={12} md={6}>
            <RichTextField
              label="Successes (Comprehensive)"
              isReadOnly={isReadOnly}
              value={successes}
              editorKey={successesRevertKey}
              onChange={createBoundedChangeHandler(
                setSuccesses,
                MAX_LENGTH,
                setSuccessesRevertKey,
                "Successes",
              )}
              isActive={activeEditorField === "Successes"}
              onActivate={() => setActiveEditorField("Successes")}
              onDeactivate={handleEditorBlur}
              charCount={stripHtml(successes).length}
              maxLength={MAX_LENGTH}
              onAIClick={() => openAIPrompt("Successes")}
              aiColor="#3299FF"
              fieldName="Successes"
              onImagePending={handleImagePending}
              pendingImages={pendingImages}
              existingAttachments={existingAttachments}
              downloadImageAsBlobUrl={downloadAttachmentImageAsBlobUrl}
            />
          </Grid>
        </Grid>
        {/* ASTT-7319 follow-up: same spacing={2}/no-padding-class fix as
            Challenges/Successes above -- same reasoning applies even
            though this is a single full-width item, not a paired row. */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12}>
            <RichTextField
              label="Readiness Notes"
              isReadOnly={isReadOnly}
              value={readlinessNotes}
              editorKey={readinessRevertKey}
              onChange={createBoundedChangeHandler(
                setReadlinessNotes,
                MAX_LENGTH,
                setReadinessRevertKey,
                "Readiness Notes",
              )}
              isActive={activeEditorField === "ReadinessNotes"}
              onActivate={() => setActiveEditorField("ReadinessNotes")}
              onDeactivate={handleEditorBlur}
              charCount={stripHtml(readlinessNotes).length}
              maxLength={MAX_LENGTH}
              onAIClick={() => openAIPrompt("ReadinessNotes")}
              aiColor="#3299FF"
              fieldName="ReadinessNotes"
              onImagePending={handleImagePending}
              pendingImages={pendingImages}
              existingAttachments={existingAttachments}
              downloadImageAsBlobUrl={downloadAttachmentImageAsBlobUrl}
            />
          </Grid>
        </Grid>
      </Grid>

      {/* ASTT-7319: Email Distro (with its Add/Update button) paired with
          Attachments in one row -- see TrainingNotes.js's identical
          restructuring and comment for full reasoning, applied here for
          consistency across both screens. */}
      <Grid container>
        <Grid item xs={12} sm={12} md={6} className={classes.padding1Rem}>
          {/* ASTT-7319: no external FieldLabel here -- DtrEmailDistro.js
              already renders its own "Email Distro" FieldLabel
              internally. */}
          <Box sx={{ display: "flex", alignItems: "flex-end", gap: 1 }}>
            {/* ASTT-7319 follow-up: was flexGrow: 1 -- maxWidth caps it
                to a fixed, reduced width, matching TrainingNotes.js's
                identical fix. */}
            {/* ASTT-7319 follow-up: minWidth doubled (260 -> 520), per
                direct request, matching TrainingNotes.js's identical fix. */}
            <Box sx={{ width: "fit-content", minWidth: 520 }}>
              <DtrEmailDistro
                open={showModal}
                isUpdate={isDistroEdit}
                onClose={() => setShowModal(false)}
                updateEmailDistro={updateEmailDistro}
                distroId={distroId}
                disabled={isReadOnly}
              />
            </Box>
            {/* ASTT-7319: implements the UX suggestion from the previous
                thread -- matches TrainingNotes.js's identical demotion. */}
            {/* ASTT-7319 follow-up: optional chaining + ?? "" added --
                the exact line that would have crashed this tab the same
                way TrainingNotes.js's identical line did. */}
            {(emailDistroValue?.DistroName ?? "") === "" ? (
              <CustomOutlinedButton
                variant="outlined"
                size="small"
                disabled={isReadOnly}
                onClick={() => handleDistroAddorEdit(false)}
              >
                Add
              </CustomOutlinedButton>
            ) : (
              <CustomOutlinedButton
                variant="outlined"
                size="small"
                disabled={isReadOnly}
                onClick={() => handleDistroAddorEdit(true)}
              >
                Update
              </CustomOutlinedButton>
            )}
          </Box>
        </Grid>
        <Grid item xs={12} sm={12} md={6} className={classes.padding1Rem}>
          <FieldLabel>Attachments</FieldLabel>
          {/* ASTT-7319: Summary now renders above the drop-zone control,
              was below it; mb: 1.5 gives vertical padding between them,
              matching TrainingNotes.js's identical fix. */}
          <Box sx={{ mb: 1.5 }}>
            <DTRAttachmentSummary
              fileCount={fileCount}
              fileSizeBytes={fileSizeBytes}
            />
          </Box>
          <DTRAttachmentList
            existingAttachments={existingFileAttachments}
            isReadOnly={isReadOnly}
            onPendingFilesChange={handlePendingFilesChange}
            onExistingRemoved={handleExistingAttachmentRemoved}
            resetSignal={pendingFilesResetKey}
          />
        </Grid>
      </Grid>

      {/* ASTT-7319: sticky so the Send/Save action stays reachable
          regardless of how tall the tab's content is, rather than
          requiring a scroll to the very bottom every time. */}
      <Grid
        container
        sx={{
          position: "sticky",
          bottom: 0,
          backgroundColor: "#fff",
          zIndex: 2,
          // ASTT-7319 follow-up: borderTop removed, per direct request.
        }}
      >
        {/* ASTT-7319: Email Distro + Add/Update button moved out of this
            sticky row entirely -- see TrainingNotes.js's identical
            restructuring, applied here for consistency. Just the Send/
            Save action now, widened to xs={12}. */}
        <Grid item xs={12} className={classes.padding1Rem}>
          {/* ASTT-7178: visible, specific reason -- not just a disabled
              button with no explanation. Sits directly above the action
              row so it's impossible to miss right before Save/Send. */}
          {hasSentConflict && (
            <Box
              sx={{
                mb: 1,
                px: 1.5,
                py: 1,
                borderRadius: "6px",
                backgroundColor: "#FDECEA",
                border: "1px solid #d32f2f",
                color: "#d32f2f",
                fontSize: "0.8rem",
                ...fontStyles,
              }}
            >
              Roster(s) {conflictingRosterIds.join(", ")} already{" "}
              {conflictingRosterIds.length === 1 ? "has" : "have"} a Sent
              Hand-Off report for this report type. Remove{" "}
              {conflictingRosterIds.length === 1 ? "it" : "them"} from the
              selection above to Save or Send.
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            {/* ASTT-7319: Preview button, per direct stakeholder-demo
                feedback -- see TrainingNotes.js's identical comment for
                why this sits outside the !isReadOnly check below. */}
            {handOffReportId > 0 && (
              <CustomOutlinedButton
                variant="outlined"
                size="small"
                onClick={() => setPreviewDialogOpen(true)}
              >
                Preview Email
              </CustomOutlinedButton>
            )}
            {/* ASTT-7319: replaced two separate, equal-weight buttons
                with a shared split-button -- Send is now the primary,
                visually prominent action, Save as Draft is secondary. */}
            {!isReadOnly && (
              <SaveOrSendButton
                onSendEmail={handleSendEmail}
                onSaveAsDraft={handleSaveAsDraft}
                disabled={isReadOnly || hasSentConflict}
              />
            )}
          </Box>
        </Grid>
      </Grid>
      <EmailPreviewDialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        fetchPreview={() => previewHandOffEmail(handOffReportId)}
      />

      {/* )} */}

      <SnackBar snack={snack} snackReset={snackReset}></SnackBar>
      <UnsavedChangesBlockerDialog
        blocker={navigationBlocker}
        entityLabel="Hand-Off Report"
      />
    </ThemeProvider>
  );
}

HandOffReport.propTypes = {
  rosterId: PropTypes.string,
  onDirtyChange: PropTypes.func,
  classRequestId: PropTypes.string,
  reportType: PropTypes.string,
  onReportTypeChange: PropTypes.func,
  onHandOffSaved: PropTypes.func,
  rosterStatusList: PropTypes.arrayOf(
    PropTypes.shape({
      rosterId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      handOffStatus: PropTypes.string,
    }),
  ),
};
